// Кнопка аккаунта в тулбаре: гостю — «Войти», вошедшему — его имя или почта.
// Клик открывает выпадающее меню (AccountMenu), а не попап целиком: язык,
// поездки, тарифы, настройки и персонализация — разные по весу действия, и
// прятать их все за одним окном означало лишний клик для самого частого пути.
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useT } from '../i18n';
import { useAuth } from './AuthProvider';
import { AccountMenu } from './AccountMenu';
import { AccountSettingsModal } from './AccountSettingsModal';
import { PersonalizationModal } from '../theme';
import styles from './AccountButton.module.css';

function UserIcon(): ReactElement {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="8" r="3.4" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </svg>
  );
}

export interface AccountButtonProps {
  /** Открыть форму входа сразу — например, по ссылке из письма о сбросе пароля. */
  initialSignIn?: boolean;
  /**
   * Развернуть содержимое сразу, без кнопки и без всплывающей панели.
   *
   * Нужно там, где кнопка и так стоит внутри выдвижной панели — в бургере на
   * узком экране. Панель поверх панели человек читает как сбой: два наложенных
   * слоя, у каждого свой фон и своя граница, и непонятно, какой из них
   * закроется первым. Внутри бургера места хватает, прятать нечего.
   */
  inline?: boolean;
  /**
   * Пробрасывается в AccountMenu: скрыть пункт «Выйти» из списка, потому что
   * вызывающий экран рисует его сам в другом месте (см. AccountMenu.tsx и
   * MobileShellHeader — там это футер сайдбара, прибитый к низу панели).
   * Имеет смысл только вместе с `inline`: не встроенный попап всегда рисует
   * список целиком, прятать выход в нём не от кого.
   */
  hideSignOut?: boolean;
}

export function AccountButton({
  initialSignIn = false,
  inline = false,
  hideSignOut = false,
}: AccountButtonProps): ReactElement {
  const t = useT();
  const { status, user, requestSignIn } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [personOpen, setPersonOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (initialSignIn) requestSignIn();
  }, [initialSignIn, requestSignIn]);

  /**
   * Закрыть меню и вернуть фокус на кнопку — путь для Esc и для пунктов
   * меню, которые не уводят на другой экран сами (открывают модалку,
   * выходят из аккаунта). Фокус переставляется синхронно, до того как
   * React отмонтирует пункт меню: иначе браузер сам уронит фокус на
   * <body>, и модалка, которая откроется следом, запомнит для возврата
   * не кнопку аккаунта, а пустоту.
   *
   * Клик мимо меню (см. onDown ниже) через этот путь не идёт: там фокус
   * и так уже встал туда, куда кликнули, — перетягивать его на кнопку
   * означало бы вырывать его обратно у того, что человек только что выбрал.
   */
  function closeMenu(): void {
    setMenuOpen(false);
    triggerRef.current?.focus();
  }

  // Клик вне меню и Esc закрывают его так же, как соседний попап снимков
  // (см. sync/SnapshotControls) — тот же жест, то же поведение.
  useEffect(() => {
    if (!menuOpen) return;
    function onDown(e: MouseEvent): void {
      if (!boxRef.current?.contains(e.target as Node)) setMenuOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') closeMenu();
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuOpen]);

  // Пока идёт первый /api/auth/me, кнопка занимает своё место, но не мигает
  // «Войти» перед тем, как выясниться, что пользователь уже вошёл.
  if (status === 'loading') {
    return <span className={styles.placeholder} aria-hidden="true" />;
  }

  // Развёрнутый вид: тот же набор пунктов, но без своей рамки и без кнопки —
  // рамку и заголовок ему даёт панель, внутри которой он стоит. Модалки
  // остаются здесь же: открывает их отсюда тот же код, что и в обычном виде.
  if (inline) {
    return (
      <>
        <AccountMenu
          inline
          hideSignOut={hideSignOut}
          onClose={() => {}}
          onOpenSettings={() => setSettingsOpen(true)}
          onOpenPersonalization={() => setPersonOpen(true)}
        />
        {settingsOpen && <AccountSettingsModal onClose={() => setSettingsOpen(false)} />}
        {personOpen && <PersonalizationModal onClose={() => setPersonOpen(false)} />}
      </>
    );
  }

  return (
    <div className={styles.box} ref={boxRef}>
      {/* Без aria-haspopup: панель — не меню и не диалог, а обычный набор
          настроек и ссылок под кнопкой-раскрывашкой. aria-expanded этого
          достаточно — он и так говорит скринридеру, что кнопка что-то
          открывает и в каком она сейчас состоянии. */}
      <button
        ref={triggerRef}
        type="button"
        className={`btn btn-sm ${styles.account}`}
        aria-expanded={menuOpen}
        onClick={() => setMenuOpen((v) => !v)}
        title={user ? user.email : t('account.guest.title')}
      >
        <UserIcon />
        <span className={styles.label}>{user ? user.name || user.email : t('account.title')}</span>
        {user && !user.emailVerified && (
          <span className={styles.dot} title={t('account.unverified')} aria-hidden="true" />
        )}
      </button>

      {menuOpen && (
        <AccountMenu
          onClose={closeMenu}
          onOpenSettings={() => {
            closeMenu();
            setSettingsOpen(true);
          }}
          onOpenPersonalization={() => {
            closeMenu();
            setPersonOpen(true);
          }}
        />
      )}

      {settingsOpen && <AccountSettingsModal onClose={() => setSettingsOpen(false)} />}
      {personOpen && <PersonalizationModal onClose={() => setPersonOpen(false)} />}
    </div>
  );
}
